import { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';

import { buildDynamicQrValue, hexEpochSeconds } from '@/lib/dynamic-qr';

interface DynamicQRCodeProps {
  /** Raw ONDC authorization token (the static block). */
  staticBlock: string;
  /** Static non-zero coordinates embedded in the dynamic block. */
  lat: string;
  lng: string;
  /** Client regen cadence (seconds); kept well under the gate's 30s window. */
  refreshSeconds: number;
  /**
   * serverClock − clientClock (ms). Corrects a skewed device clock so the
   * emitted timestamp tracks real (gate) time rather than the phone's clock.
   */
  offsetMs: number;
}

/**
 * DynamicQRCode renders the BMRCL gate QR and regenerates its dynamic block
 * every `refreshSeconds`, entirely client-side (no network) so it keeps working
 * at an underground gate once the ticket is open. The timestamp is offset-
 * corrected to the server clock; EC level "L" keeps the (long) symbol sparse for
 * easy scanning off a backlit screen.
 */
export function DynamicQRCode({
  staticBlock,
  lat,
  lng,
  refreshSeconds,
  offsetMs,
}: DynamicQRCodeProps) {
  // Keep the latest offset in a ref so a new poll updates it without resetting
  // the interval (the interval only re-subscribes when the token/coords change).
  const offsetRef = useRef(offsetMs);
  offsetRef.current = offsetMs;

  const [value, setValue] = useState(() =>
    buildDynamicQrValue(staticBlock, hexEpochSeconds(Date.now() + offsetMs), lat, lng)
  );

  useEffect(() => {
    const tick = () =>
      setValue(
        buildDynamicQrValue(staticBlock, hexEpochSeconds(Date.now() + offsetRef.current), lat, lng)
      );
    tick(); // refresh immediately on (re)mount or when the token/coords change
    const id = window.setInterval(tick, Math.max(1, refreshSeconds) * 1000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [staticBlock, lat, lng, refreshSeconds]);

  return (
    <QRCodeSVG
      value={value}
      size={256}
      level="L"
      marginSize={2}
      className="mx-auto h-64 w-64 rounded-lg bg-white p-2"
    />
  );
}
